/**
 * SHIPERBOX — MOBILE DESIGN SYSTEM (V3: Cool Pastels Edition)
 * ---------------------------------------------------------------
 * One shared token set so every screen reads as the same product.
 * Now enhanced with Light/Dark Mode support via CSS Variables.
 */
import React, { createContext, useContext, useState, useEffect } from "react";

// ==========================================
// THEME CONTEXT & PROVIDER
// ==========================================
export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const savedTheme = localStorage.getItem("shiperbox-theme");
    if (savedTheme) {
      setTheme(savedTheme);
    }
    // No saved preference: always default to light, regardless of the
    // device's system dark-mode setting.
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => {
      const nextTheme = prev === "light" ? "dark" : "light";
      localStorage.setItem("shiperbox-theme", nextTheme);
      return nextTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <div
        data-theme={theme}
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          backgroundColor: "var(--mist)",
          transition: "background-color 0.35s ease",
        }}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

// ==========================================
// DESIGN TOKENS (Mapped to CSS Variables)
// ==========================================
export const COLOR = {
  primary: "var(--primary)",
  primaryDark: "var(--primaryDark)",
  primarySoft: "var(--primarySoft)",
  
  accent: "var(--accent)",
  accentDark: "var(--accentDark)",
  accentSoft: "var(--accentSoft)",
  
  pop: "var(--pop)",
  popSoft: "var(--popSoft)",
  onPrimary: "var(--onPrimary)",

  ink: "var(--ink)",
  paper: "var(--paper)",
  mist: "var(--mist)",
  line: "var(--line)",
  muted: "var(--muted)",
  surface: "var(--surface)",
  overlay: "var(--overlay)",

  error: "var(--error)",
  errorSoft: "var(--errorSoft)",
  success: "var(--success)",
  successSoft: "var(--successSoft)",
  warning: "var(--warning)",
  warningSoft: "var(--warningSoft)",

  discountBg: "var(--discountBg)",
  discountText: "var(--discountText)",
  placeholder: "var(--placeholder)",

  gradientHeader: "var(--gradientHeader)",
  gradientCTA: "var(--gradientCTA)",
  headerSurface: "var(--headerSurface)",
};

export const FONT = {
  display: "'Space Grotesk', 'Segoe UI', system-ui, sans-serif",
  body: "'Inter', -apple-system, sans-serif",
  mono: "'IBM Plex Mono', 'SFMono-Regular', monospace",
};

export const RADIUS = { sm: "8px", md: "14px", lg: "20px", xl: "28px", pill: "999px" };

// Fixed brand header background. Deliberately a plain string, not a CSS
// variable — the header band (logo/search bar/category strip) always stays
// this pastel blue, in both light and dark mode. Only content underneath it
// (cards, page background, text) follows the theme.
export const FIXED_HEADER_GRADIENT = "var(--headerBackground)";

export const SHADOW = {
  soft: "var(--shadowSoft)",
  card: "var(--shadowCard)",
  float: "var(--shadowFloat)",
  cta: "var(--shadowCta)",
  rest: "var(--shadowRest)",
  lift: "var(--shadowLift)",
};

export const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
export const EASE_SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

// ==========================================
// GLOBAL STYLES & CSS VARIABLES
// ==========================================
export const GlobalMobileStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

    /* LIGHT THEME VARIABLES */
    :root, [data-theme="light"] {
      --primary: #1E90FF;
      --primaryDark: #1E90FF;
      --primarySoft: rgba(30, 144, 255, 0.15);
      --accent: #1E90FF;
      --accentDark: #1E90FF;
      --accentSoft: rgba(224, 195, 252, 0.2);
      --pop: #1E90FF;
      --popSoft: rgba(153, 246, 228, 0.2);
      --onPrimary: #ffffff;
      
      --ink: #0f172a;
      --paper: #ffffff;
      --mist: #f8fafc;
      --line: #e2e8f0;
      --muted: #64748b;
      --surface: #ffffff;
      --overlay: rgba(15, 23, 42, 0.55);
      
      --error: #ef4444;
      --errorSoft: #fef2f2;
      --success: #1E90FF;
      --successSoft: rgba(30, 144, 255, 0.10);
      --warning: #1E90FF;
      --warningSoft: rgba(30, 144, 255, 0.10);

      --discountBg: #E8F8EC;
      --discountText: #0C831F;
      
      --gradientHeader: linear-gradient(135deg, #8ec5fc 0%, #8ec5fc 100%);
      --gradientCTA: linear-gradient(135deg, #1E90FF 0%, #1E90FF 100%);
      --headerSurface: #8ec5fc;
      --headerBackground: radial-gradient(120% 140% at 15% -20%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 55%), #8ec5fc;
      --headerText: #0f172a;
      --adsBackground: #8ec5fc;
      --placeholder: #94a3b8;
      
      --shadowSoft: 0 4px 15px rgba(15, 23, 42, 0.04);
      --shadowCard: 0 8px 25px rgba(15, 23, 42, 0.08);
      --shadowFloat: 0 12px 35px rgba(15, 23, 42, 0.12);
      --shadowCta: 0 8px 20px rgba(30, 144, 255, 0.4);
      --shadowRest: 0 2px 8px rgba(15, 23, 42, 0.05);
      --shadowLift: 0 14px 28px rgba(15, 23, 42, 0.12);
    }

    /* DARK THEME — Shiperbox Blue System (no yellow/orange accents) */
    [data-theme="dark"] {
      --primary: #1E90FF;
      --primaryDark: #1E90FF;
      --primarySoft: rgba(30, 144, 255, 0.16);
      --accent: #1E90FF;
      --accentDark: #1E90FF;
      --accentSoft: rgba(30, 144, 255, 0.14);
      --pop: #1E90FF;
      --popSoft: rgba(30, 144, 255, 0.14);
      --onPrimary: #ffffff;

      --ink: #f8fafc;
      --paper: #1c1c1e;
      --mist: #121212;
      --line: #2e2e30;
      --muted: #a1a1a6;
      --surface: #232326;
      --overlay: rgba(0, 0, 0, 0.72);

      --error: #ff6b6b;
      --errorSoft: rgba(255, 107, 107, 0.14);
      --success: #1E90FF;
      --successSoft: rgba(30, 144, 255, 0.14);
      --warning: #1E90FF;
      --warningSoft: rgba(30, 144, 255, 0.14);

      --discountBg: rgba(30, 144, 255, 0.14);
      --discountText: #1E90FF;

      --gradientHeader: linear-gradient(135deg, #172554 0%, #0f1b3d 100%);
      --gradientCTA: linear-gradient(135deg, #1E90FF 0%, #1E90FF 100%);
      --headerSurface: #172554;
      --headerBackground: linear-gradient(135deg, #172554 0%, #0f1b3d 100%);
      --headerText: #ffffff;
      --adsBackground: #172554;
      --placeholder: #7c8aa5;

      --shadowSoft: 0 4px 15px rgba(0, 0, 0, 0.30);
      --shadowCard: 0 8px 25px rgba(0, 0, 0, 0.42);
      --shadowFloat: 0 12px 35px rgba(0, 0, 0, 0.52);
      --shadowCta: 0 8px 20px rgba(30, 144, 255, 0.32);
      --shadowRest: 0 2px 8px rgba(0, 0, 0, 0.28);
      --shadowLift: 0 14px 28px rgba(0, 0, 0, 0.50);
    }

    /* Smooth theme switch: fade every surface's color-related props together.
       More specific .sb-tap / .sb-lift transitions below override this list
       on interactive elements, so tap/press feel is untouched. */
    *, *::before, *::after {
      transition: background-color 0.3s ${EASE}, border-color 0.3s ${EASE}, color 0.3s ${EASE}, box-shadow 0.3s ${EASE}, fill 0.3s ${EASE};
    }

    html, body, #root {
      width: 100%;
      min-width: 0;
      margin: 0;
      padding: 0;
    }
    html {
      -webkit-text-size-adjust: 100%;
      text-size-adjust: 100%;
      overscroll-behavior-x: none;
    }
    body {
      min-height: 100vh;
      min-height: 100dvh;
      overflow-x: hidden;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    #root {
      min-height: 100vh;
      min-height: 100dvh;
      overflow-x: clip;
    }
    button, input, textarea, select {
      font: inherit;
      -webkit-tap-highlight-color: transparent;
    }
    button, a, [role="button"] { touch-action: manipulation; }
    img, svg, video, canvas { max-width: 100%; }
    input, textarea, select { max-width: 100%; box-sizing: border-box; }

    /* Dark theme readability: header location + category names stay white. */
    [data-theme="dark"] .sb-location-text,
    [data-theme="dark"] .sb-location-address,
    [data-theme="dark"] .sb-location-deliver-to,
    [data-theme="dark"] .sb-category-title,
    [data-theme="dark"] .mil-side-item,
    [data-theme="dark"] .mil-side-item span,
    [data-theme="dark"] .mil-cat-label {
      color: #ffffff !important;
    }

    /* Snappy, modern animations */
    @keyframes sb-fadeSlide { from { opacity:0; transform: translateY(8px);} to { opacity:1; transform:none; } }
    @keyframes sb-pop { 0% { transform: scale(0.85); opacity:0; } 100% { transform: scale(1); opacity:1; } }
    @keyframes sb-pulseGlow { 0% { box-shadow: 0 0 0 0 rgba(30, 144, 255,0.4);} 70% { box-shadow: 0 0 0 16px rgba(30, 144, 255,0);} 100% { box-shadow: 0 0 0 0 rgba(30, 144, 255,0);} }
    @keyframes sb-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
    @keyframes sb-shake { 10%,90% { transform: translateX(-1px);} 20%,80% { transform: translateX(2px);} 30%,50%,70% { transform: translateX(-4px);} 40%,60% { transform: translateX(4px);} }

    /* Dark mode: opt-in utility for a heading that needs to be forced pure
       white regardless of its own inline color. Plain h1-h6 are intentionally
       NOT targeted here anymore — every heading in the app already colors
       itself with a theme-aware token (COLOR.ink, COLOR.primaryDark, etc.),
       so a blanket override would strip brand-colored headings (e.g. the
       blue "Important Info" note titles) down to plain white. */
    [data-theme="dark"] .sb-heading {
      color: #ffffff !important;
    }

    /* Interactive feedback states for inputs and buttons */
    .sb-field:focus-within { border-color: var(--primaryDark) !important; background: var(--paper) !important; box-shadow: 0 0 0 4px var(--primarySoft); }

    /* Theme-specific brand colors: Light #1E90FF, Dark #1E90FF. */
    [data-theme="light"] .sb-dark-add-btn,
    :root .sb-dark-add-btn {
      background: #1E90FF !important;
      color: #ffffff !important;
      border-color: #1E90FF !important;
    }

    [data-theme="dark"] .sb-dark-add-btn {
      background: #1E90FF !important;
      color: #ffffff !important;
      border-color: #1E90FF !important;
    }
    .sb-tap { transition: transform 0.18s ${EASE_SPRING}, box-shadow 0.3s ${EASE}, filter 0.18s ${EASE}, background-color 0.3s ${EASE}, border-color 0.3s ${EASE}, color 0.3s ${EASE}; }
    .sb-tap:active { transform: scale(0.96); transition: transform 0.1s ease; }
    .sb-shake { animation: sb-shake 0.42s; }

    .sb-lift { box-shadow: var(--shadowRest); transition: transform 0.22s ${EASE_SPRING}, box-shadow 0.3s ${EASE}, background-color 0.3s ${EASE}, border-color 0.3s ${EASE}, color 0.3s ${EASE}; }
    .sb-lift:active { transform: translateY(1px) scale(0.97); box-shadow: var(--shadowRest); }

    .sb-skeleton {
      background: linear-gradient(90deg, var(--line) 0%, var(--mist) 50%, var(--line) 100%);
      background-size: 800px 100%;
      animation: sb-shimmer 1.6s linear infinite;
    }

    /* Clean UI by hiding native scrollbars */
    ::-webkit-scrollbar { display: none; }
  `}</style>
);

// Individual named exports (in addition to COLOR.xxx)
export const {
  primary: PRIMARY, primaryDark: PRIMARY_DARK, primarySoft: PRIMARY_SOFT,
  accent: ACCENT, accentDark: ACCENT_DARK, accentSoft: ACCENT_SOFT,
  pop: POP, popSoft: POP_SOFT, onPrimary: ON_PRIMARY,
  ink: INK, paper: PAPER, mist: MIST, line: LINE, muted: MUTED,
  error: ERROR, errorSoft: ERROR_SOFT, success: SUCCESS, successSoft: SUCCESS_SOFT,
  gradientHeader: GRADIENT_HEADER, gradientCTA: GRADIENT_CTA,
} = COLOR;

// Reusable perforation "tear line" 
export const Perforation = ({ margin = "20px 0" }) => (
  <div
    style={{
      height: "14px",
      margin,
      backgroundImage: `radial-gradient(circle, ${COLOR.line} 2px, transparent 2px)`,
      backgroundSize: "14px 100%",
      backgroundRepeat: "repeat-x",
      backgroundPosition: "center",
    }}
  />
);

export const Eyebrow = ({ children, tone = "primary" }) => {
  const bg = tone === "accent" ? COLOR.accentSoft : tone === "pop" ? COLOR.popSoft : COLOR.primarySoft;
  const fg = tone === "accent" ? COLOR.accentDark : tone === "pop" ? COLOR.primaryDark : COLOR.primaryDark;
  
  return (
    <span
      style={{
        fontFamily: FONT.mono, fontSize: "11px", fontWeight: 700,
        letterSpacing: "0.12em", color: fg, backgroundColor: bg,
        padding: "5px 12px", borderRadius: RADIUS.pill, display: "inline-block",
      }}
    >
      {children}
    </span>
  );
};

// Light/Dark mode toggle switch (sun / moon pill)
export const ThemeToggle = ({ size = "md" }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const dims = size === "sm" ? { w: 52, h: 28, knob: 22 } : { w: 60, h: 32, knob: 26 };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label="Toggle dark mode"
      onClick={toggleTheme}
      className="sb-tap"
      style={{
        width: `${dims.w}px`,
        height: `${dims.h}px`,
        borderRadius: RADIUS.pill,
        border: `1px solid ${COLOR.line}`,
        background: isDark ? COLOR.ink : COLOR.mist,
        position: "relative",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
        transition: `background-color 0.25s ${EASE}`,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "2px",
          left: isDark ? `${dims.w - dims.knob - 3}px` : "2px",
          width: `${dims.knob}px`,
          height: `${dims.knob}px`,
          borderRadius: "50%",
          background: isDark ? COLOR.paper : "#ffffff",
          boxShadow: SHADOW.rest,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: `${dims.knob - 12}px`,
          transition: `left 0.25s ${EASE_SPRING}`,
        }}
      >
        {isDark ? "🌙" : "☀️"}
      </span>
    </button>
  );
};

// Lowercase aliases
export const themeToggle = ThemeToggle;
export const perforation = Perforation;
export const eyebrow = Eyebrow;
export const globalMobileStyles = GlobalMobileStyles;
export const color = COLOR;
export const font = FONT;
export const radius = RADIUS;
export const shadow = SHADOW;
export const ease = EASE;
export const easeSpring = EASE_SPRING;